import { createPortal } from "react-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../utils/cn";

// ── Icons ─────────────────────────────────────────────────────────────────────

function SortAscIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

function SortDescIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M12 5v14M5 12l7 7 7-7" />
    </svg>
  );
}

function SortDefaultIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" className="opacity-40">
      <path d="M7 15l5-5 5 5M7 9l5 5 5-5" />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}

function EmptyTableIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1" aria-hidden="true"
      className="text-content-tertiary opacity-50">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 9v12" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface DataTableColumn<T = any> {
  key: string;
  header: string;
  width?: string;
  sortable?: boolean;
  render?: (value: T, row: T) => React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface DataTableAction<T = any> {
  label: string;
  onClick: (row: T) => void;
  variant?: "default" | "danger";
  show?: (row: T) => boolean;
}

export interface DataTablePagination {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  totalItems: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface DataTableProps<T = any> {
  columns: DataTableColumn<T>[];
  data: T[];
  isLoading?: boolean;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  selectedRows?: string[];
  onSelectRow?: (id: string) => void;
  onSelectAll?: (ids: string[]) => void;
  idKey?: string;
  stickyHeader?: boolean;
  pagination?: DataTablePagination;
  actions?: DataTableAction<T>[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function TableCheckbox({
  checked,
  indeterminate,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  label: string;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={label}
      className="w-4 h-4 rounded border-border-strong cursor-pointer accent-violet-600 dark:accent-violet-400"
    />
  );
}

function getPageWindow(page: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "…")[] = [1];
  if (page > 3) pages.push("…");
  const lo = Math.max(2, page - 1);
  const hi = Math.min(total - 1, page + 1);
  for (let i = lo; i <= hi; i++) pages.push(i);
  if (page < total - 2) pages.push("…");
  pages.push(total);
  return pages;
}

function PaginationBar({ pagination }: { pagination: DataTablePagination }) {
  const { page, totalPages, onPageChange, pageSize, totalItems } = pagination;
  const start = Math.min((page - 1) * pageSize + 1, totalItems);
  const end   = Math.min(page * pageSize, totalItems);
  const pages = getPageWindow(page, totalPages);

  const btnBase = cn(
    "min-w-[32px] h-8 px-2 flex items-center justify-center rounded-md text-sm",
    "transition-colors duration-100",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple",
  );

  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-border-default">
      <p className="text-sm text-content-secondary">
        {totalItems === 0
          ? "No results"
          : `Showing ${start}–${end} of ${totalItems}`}
      </p>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className={cn(btnBase, "text-content-secondary hover:text-content-primary hover:bg-surface-overlay disabled:opacity-40 disabled:cursor-not-allowed")}
          aria-label="Previous page"
        >
          <ChevronLeftIcon />
        </button>

        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`sep-${i}`} className="min-w-[32px] h-8 flex items-center justify-center text-sm text-content-tertiary">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p as number)}
              aria-current={page === p ? "page" : undefined}
              className={cn(
                btnBase,
                page === p
                  ? "bg-brand-purple text-white font-medium"
                  : "text-content-secondary hover:text-content-primary hover:bg-surface-overlay",
              )}
            >
              {p}
            </button>
          ),
        )}

        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className={cn(btnBase, "text-content-secondary hover:text-content-primary hover:bg-surface-overlay disabled:opacity-40 disabled:cursor-not-allowed")}
          aria-label="Next page"
        >
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

const SHIMMER_WIDTHS = ["68%", "45%", "58%", "72%", "50%"];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  isLoading = false,
  emptyMessage,
  onRowClick,
  selectedRows,
  onSelectRow,
  onSelectAll,
  idKey = "id",
  stickyHeader = false,
  pagination,
  actions,
}: DataTableProps<T>) {
  const hasCheckbox = !!onSelectRow;
  const hasActions  = !!(actions?.length);
  const colSpan     = columns.length + (hasCheckbox ? 1 : 0) + (hasActions ? 1 : 0);

  // ── Sort ──────────────────────────────────────────────────────────────────
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  const handleHeaderClick = (col: DataTableColumn<T>) => {
    if (!col.sortable) return;
    if (sortKey === col.key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(col.key); setSortDir("asc"); }
  };

  // ── Selection ─────────────────────────────────────────────────────────────
  const allIds      = data.map((r) => String(r[idKey]));
  const selectedSet = new Set(selectedRows ?? []);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedSet.has(id));
  const someSelected = allIds.some((id) => selectedSet.has(id));

  const handleSelectAll = () =>
    onSelectAll?.(allSelected ? [] : allIds);

  // ── Actions dropdown ──────────────────────────────────────────────────────
  const [actionMenu, setActionMenu] = useState<{ row: T; top: number; right: number } | null>(null);
  const actionBtnRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  useEffect(() => {
    if (!actionMenu) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as Element).closest("[data-action-menu]")) setActionMenu(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [actionMenu]);

  const openActionMenu = (row: T) => {
    const rowId = String(row[idKey]);
    const btn   = actionBtnRefs.current.get(rowId);
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setActionMenu({ row, top: rect.bottom + 4, right: window.innerWidth - rect.right });
  };

  return (
    <div className="flex flex-col rounded-card overflow-hidden border border-border-default bg-white dark:bg-surface-raised shadow-sm dark:shadow-none">
      {/* Table scroll wrapper */}
      <div className={cn("overflow-x-auto", stickyHeader && "overflow-y-auto max-h-[600px]")}>
        <table className="w-full border-collapse text-sm">
          {/* Head */}
          <thead className={cn(
            "bg-surface-raised dark:bg-surface-overlay",
            "border-b border-border-default",
            stickyHeader && "sticky top-0 z-10",
          )}>
            <tr>
              {hasCheckbox && (
                <th scope="col" className="w-10 px-4 py-3 border-b border-border-default">
                  <TableCheckbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={handleSelectAll}
                    label="Select all rows"
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  onClick={() => handleHeaderClick(col)}
                  style={col.width ? { width: col.width } : undefined}
                  className={cn(
                    "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider",
                    "text-content-secondary border-b border-border-default",
                    col.sortable && "cursor-pointer select-none hover:text-content-primary",
                    sortKey === col.key && "text-brand-purple",
                  )}
                >
                  <div className="flex items-center gap-1.5">
                    {col.header}
                    {col.sortable && (
                      <span className={sortKey === col.key ? "text-brand-purple" : "text-content-tertiary"}>
                        {sortKey === col.key
                          ? sortDir === "asc" ? <SortAscIcon /> : <SortDescIcon />
                          : <SortDefaultIcon />}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              {hasActions && (
                <th scope="col" className="w-12 px-4 py-3 border-b border-border-default" />
              )}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-border-default/40">
                  {hasCheckbox && (
                    <td className="px-4 py-3.5">
                      <div className="w-4 h-4 rounded skeleton-shimmer" />
                    </td>
                  )}
                  {columns.map((col, ci) => (
                    <td key={col.key} className="px-4 py-3.5">
                      <div
                        className="h-4 rounded-md skeleton-shimmer"
                        style={{ width: SHIMMER_WIDTHS[(i + ci) % 5] }}
                      />
                    </td>
                  ))}
                  {hasActions && (
                    <td className="px-4 py-3.5">
                      <div className="w-6 h-6 rounded skeleton-shimmer ml-auto" />
                    </td>
                  )}
                </tr>
              ))
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-14 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <EmptyTableIcon />
                    <p className="text-sm text-content-secondary">
                      {emptyMessage ?? "No data available"}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              sortedData.map((row, rowIdx) => {
                const rowId     = String(row[idKey]);
                const isSelected = selectedSet.has(rowId);
                return (
                  <tr
                    key={rowId}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cn(
                      "border-b border-border-default/40 transition-colors duration-100",
                      rowIdx % 2 === 1 && !isSelected && "bg-surface-raised/40 dark:bg-surface-overlay/30",
                      isSelected
                        ? "bg-violet-50 dark:bg-violet-900/20"
                        : "hover:bg-surface-raised dark:hover:bg-surface-elevated",
                      onRowClick && "cursor-pointer",
                    )}
                  >
                    {hasCheckbox && (
                      <td
                        className="px-4 py-3.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TableCheckbox
                          checked={isSelected}
                          onChange={() => onSelectRow?.(rowId)}
                          label={`Select row ${rowId}`}
                        />
                      </td>
                    )}
                    {columns.map((col) => {
                      const value = row[col.key];
                      return (
                        <td
                          key={col.key}
                          className={cn(
                            "px-4 py-3.5 text-sm text-content-primary",
                            sortKey === col.key && "bg-violet-50/20 dark:bg-violet-500/5",
                          )}
                        >
                          {col.render
                            ? col.render(value, row)
                            : value == null ? "" : String(value)}
                        </td>
                      );
                    })}
                    {hasActions && (
                      <td
                        className="px-4 py-3.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          ref={(el) => {
                            if (el) actionBtnRefs.current.set(rowId, el);
                            else actionBtnRefs.current.delete(rowId);
                          }}
                          type="button"
                          onClick={() => openActionMenu(row)}
                          aria-label="Row actions"
                          className={cn(
                            "p-1.5 rounded-md transition-colors duration-100 block ml-auto",
                            "text-content-tertiary hover:text-content-primary",
                            "hover:bg-surface-overlay dark:hover:bg-surface-overlay",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple",
                          )}
                        >
                          <DotsIcon />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && <PaginationBar pagination={pagination} />}

      {/* Actions dropdown portal */}
      {actionMenu && typeof document !== "undefined" && createPortal(
        <div
          data-action-menu
          className={cn(
            "fixed z-[1060] min-w-[160px] py-1 rounded-md overflow-hidden",
            "bg-white dark:bg-surface-raised",
            "shadow-lg border border-border-default",
          )}
          style={{ top: actionMenu.top, right: actionMenu.right }}
        >
          {actions?.map((action, i) => {
            if (action.show && !action.show(actionMenu.row)) return null;
            return (
              <button
                key={i}
                type="button"
                onClick={() => { action.onClick(actionMenu.row); setActionMenu(null); }}
                className={cn(
                  "w-full px-4 py-2.5 text-left text-sm transition-colors duration-100",
                  action.variant === "danger"
                    ? "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30"
                    : "text-content-primary hover:bg-surface-overlay",
                )}
              >
                {action.label}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

DataTable.displayName = "DataTable";
